import { useEffect } from 'react'
import clsx from 'clsx'
import { gsap, Elastic } from 'gsap'
import { useStaticQuery, graphql } from 'gatsby'
import { GatsbyImage } from 'gatsby-plugin-image'
import { ScrollTrigger } from 'gsap/ScrollTrigger'
import { SplitText } from 'gsap/SplitText'

import style from './Slider.module.css'

const MSSlider = () => {
  const data = useStaticQuery(graphql`
    {
      vmimage: file(relativePath: { eq: "services/managed/vmanagement.png" }) {
        childImageSharp {
          gatsbyImageData(width: 379, placeholder: TRACED_SVG, layout: FIXED)
        }
      }
      appsecimage: file(relativePath: { eq: "services/managed/applicationsec.png" }) {
        childImageSharp {
          gatsbyImageData(width: 403, placeholder: TRACED_SVG, layout: FIXED)
        }
      }
      hostandendpoint: file(relativePath: { eq: "services/managed/hostandendpoint.png" }) {
        childImageSharp {
          gatsbyImageData(width: 473, placeholder: TRACED_SVG, layout: FIXED)
        }
      }
      network: file(relativePath: { eq: "services/managed/networksecurity.png" }) {
        childImageSharp {
          gatsbyImageData(width: 380, placeholder: TRACED_SVG, layout: FIXED)
        }
      }
      threat: file(relativePath: { eq: "services/managed/threatdetection.png" }) {
        childImageSharp {
          gatsbyImageData(width: 475, placeholder: TRACED_SVG, layout: FIXED)
        }
      }
      compliance: file(relativePath: { eq: "services/managed/cloudsecurity.png" }) {
        childImageSharp {
          gatsbyImageData(width: 475, placeholder: TRACED_SVG, layout: FIXED)
        }
      }
      lacework: file(relativePath: { eq: "services/managed/lacework.png" }) {
        childImageSharp {
          gatsbyImageData(width: 154, placeholder: TRACED_SVG, layout: FIXED)
        }
      }
      cloudcheckr: file(relativePath: { eq: "services/managed/cloudcheckr.png" }) {
        childImageSharp {
          gatsbyImageData(width: 145, placeholder: TRACED_SVG, layout: FIXED)
        }
      }
    }
  `)
  useEffect(() => {
    gsap.registerPlugin(ScrollTrigger)
    gsap.registerPlugin(SplitText)
    const tl2 = gsap.timeline({
      scrollTrigger: {
        id: 'slider',
        trigger: `.${style.container}`,
        start: 'center center',
        scrub: true,
        pin: true,
        snap: {
          snapTo: 'labels',
          duration: { min: 0, max: 0.4 },
          delay: 0,
          ease: 'power1.inOut',
        },
        anticipatePin: 1,
        end: '+=4000',
      },
    })
    const headerText = new SplitText(`.${style.header}`, { type: 'words,chars' }).chars
    gsap.set(`.${style.header}`, { perspective: 400 })

    gsap.from(headerText, {
      duration: 0.05,
      opacity: 0,
      y: -20,
      x: -100,
      scale: 0,
      stagger: 0.02,
      ease: 'power1.easeOut',
      scrollTrigger: {
        trigger: `.${style.header}`,
        start: '-=1000',
      },
    })
    tl2.fromTo(
      '#appsecinc',
      { y: 250, opacity: 0 },
      { y: -60, duration: 4, x: -400, opacity: 1, ease: Elastic.easeOut.config(1, 1) },
      'slide5'
    )
    tl2.addLabel('slide5')
    tl2.to('.mover', { duration: 5, y: 64 }, 'slide5')
    tl2.from('#slide5', { x: -500, duration: 5, opacity: 0 }, 'slide5')
    tl2.from('#slide5text', { x: -500, duration: 4, opacity: 0 }, 'slide5')
    tl2.fromTo(
      '#hostsecinc',
      { y: 250, opacity: 0 },
      { y: 45, duration: 4, x: 0, opacity: 1, ease: Elastic.easeOut.config(1, 1) },
      'slide5'
    )

    tl2.addLabel('slide4')
    tl2.to('.mover', { duration: 5, y: '+=64' }, 'slide4')
    tl2.from('#slide4', { x: -500, duration: 5, opacity: 0 }, 'slide4')
    tl2.from('#slide4text', { x: -500, duration: 4, opacity: 0 }, 'slide4')
    tl2.fromTo(
      '#networkinc',
      { y: 250, opacity: 0 },
      { y: 0, duration: 4, x: 425, opacity: 1, ease: Elastic.easeOut.config(1, 1) },
      'slide4'
    )

    tl2.addLabel('slide3')
    tl2.to('.mover', { duration: 5, y: '+=63' }, 'slide3')
    tl2.from('#slide3', { x: -500, duration: 5, opacity: 0 }, 'slide3')
    tl2.from('#slide3text', { x: -500, duration: 4, opacity: 0 }, 'slide3')
    tl2.fromTo(
      '#threatinc',
      { y: 250, opacity: 0 },
      { y: 45, duration: 4, x: 425, opacity: 1, ease: Elastic.easeOut.config(1, 1) },
      'slide3'
    )

    tl2.addLabel('slide2')
    tl2.to('.mover', { duration: 5, y: '+=62' }, 'slide2')
    tl2.from('#slide2', { x: -500, duration: 5, opacity: 0 }, 'slide2')
    tl2.from('#slide2text', { x: -500, duration: 4, opacity: 0 }, 'slide2')
    tl2.fromTo(
      '#cloudinc',
      { y: 250, opacity: 0 },
      { y: 0, duration: 4, x: 820, opacity: 1, ease: Elastic.easeOut.config(1, 1) },
      'slide2'
    )

    tl2.addLabel('slide1')
    tl2.to('.mover', { duration: 5, y: '+=58' }, 'slide1')
    tl2.from('#slide1', { x: -500, duration: 5, opacity: 0 }, 'slide1')
    tl2.from('#slide1text', { x: -500, duration: 4, opacity: 0 }, 'slide1')
    tl2.fromTo(
      '#vminc',
      { y: 250, opacity: 0 },
      { y: 45, duration: 4, x: 880, opacity: 1, ease: Elastic.easeOut.config(1, 1) },
      'slide1'
    )

    return () => {
      ScrollTrigger.getById('slider').kill()
    }
  }, [])

  return (
    <div className={style.container}>
      <h2 className={style.header}>Great, so what exactly does this cover?</h2>
      <div className={style.includes} id="appsecinc">
        <div className={style.includeitem}>
          <svg width="42px" height="40px" viewBox="0 0 42 40">
            <defs>
              <linearGradient
                x1="14.9482157%"
                y1="84.9243045%"
                x2="85.3774435%"
                y2="16.3295925%"
                id="acheck"
              >
                <stop stopColor="#1D8DBA" offset="0%" />
                <stop stopColor="#03ECAE" offset="100%" />
              </linearGradient>
            </defs>
            <g
              transform="translate(1 .644)"
              stroke="none"
              strokeWidth={1}
              fill="none"
              fillRule="evenodd"
            >
              <rect
                strokeOpacity={0.25}
                stroke="#FFF"
                x={0}
                y={8.35603333}
                width={37}
                height={30}
                rx={1}
              />
              <path
                fill="url(#acheck)"
                d="M9.75585938 34.3560333L21.0314331 38.3560333 40.5586853 0 21.0314331 0 19.0314331 27.3560333 3.49880981 20.9473114z"
              />
            </g>
          </svg>
          <h3>APPLICATION SECURITY</h3>
        </div>
        <div className={style.includeitem} id="hostsecinc">
          <svg width="42px" height="40px" viewBox="0 0 42 40">
            <defs>
              <linearGradient
                x1="14.9482157%"
                y1="84.9243045%"
                x2="85.3774435%"
                y2="16.3295925%"
                id="acheck"
              >
                <stop stopColor="#1D8DBA" offset="0%" />
                <stop stopColor="#03ECAE" offset="100%" />
              </linearGradient>
            </defs>
            <g
              transform="translate(1 .644)"
              stroke="none"
              strokeWidth={1}
              fill="none"
              fillRule="evenodd"
            >
              <rect
                strokeOpacity={0.25}
                stroke="#FFF"
                x={0}
                y={8.35603333}
                width={37}
                height={30}
                rx={1}
              />
              <path
                fill="url(#acheck)"
                d="M9.75585938 34.3560333L21.0314331 38.3560333 40.5586853 0 21.0314331 0 19.0314331 27.3560333 3.49880981 20.9473114z"
              />
            </g>
          </svg>
          <h3>HOST &amp; ENDPOINT SECURITY</h3>
        </div>
        <div className={style.includeitem} id="networkinc">
          <svg width="42px" height="40px" viewBox="0 0 42 40">
            <defs>
              <linearGradient
                x1="14.9482157%"
                y1="84.9243045%"
                x2="85.3774435%"
                y2="16.3295925%"
                id="acheck"
              >
                <stop stopColor="#1D8DBA" offset="0%" />
                <stop stopColor="#03ECAE" offset="100%" />
              </linearGradient>
            </defs>
            <g
              transform="translate(1 .644)"
              stroke="none"
              strokeWidth={1}
              fill="none"
              fillRule="evenodd"
            >
              <rect
                strokeOpacity={0.25}
                stroke="#FFF"
                x={0}
                y={8.35603333}
                width={37}
                height={30}
                rx={1}
              />
              <path
                fill="url(#acheck)"
                d="M9.75585938 34.3560333L21.0314331 38.3560333 40.5586853 0 21.0314331 0 19.0314331 27.3560333 3.49880981 20.9473114z"
              />
            </g>
          </svg>
          <h3>NETWORK SECURITY</h3>
        </div>
        <div className={style.includeitem} id="threatinc">
          <svg width="42px" height="40px" viewBox="0 0 42 40">
            <defs>
              <linearGradient
                x1="14.9482157%"
                y1="84.9243045%"
                x2="85.3774435%"
                y2="16.3295925%"
                id="acheck"
              >
                <stop stopColor="#1D8DBA" offset="0%" />
                <stop stopColor="#03ECAE" offset="100%" />
              </linearGradient>
            </defs>
            <g
              transform="translate(1 .644)"
              stroke="none"
              strokeWidth={1}
              fill="none"
              fillRule="evenodd"
            >
              <rect
                strokeOpacity={0.25}
                stroke="#FFF"
                x={0}
                y={8.35603333}
                width={37}
                height={30}
                rx={1}
              />
              <path
                fill="url(#acheck)"
                d="M9.75585938 34.3560333L21.0314331 38.3560333 40.5586853 0 21.0314331 0 19.0314331 27.3560333 3.49880981 20.9473114z"
              />
            </g>
          </svg>
          <h3>THREAT DETECTION &amp; RESPONSE</h3>
        </div>
        <div className={style.includeitem} id="cloudinc">
          <svg width="42px" height="40px" viewBox="0 0 42 40">
            <defs>
              <linearGradient
                x1="14.9482157%"
                y1="84.9243045%"
                x2="85.3774435%"
                y2="16.3295925%"
                id="acheck"
              >
                <stop stopColor="#1D8DBA" offset="0%" />
                <stop stopColor="#03ECAE" offset="100%" />
              </linearGradient>
            </defs>
            <g
              transform="translate(1 .644)"
              stroke="none"
              strokeWidth={1}
              fill="none"
              fillRule="evenodd"
            >
              <rect
                strokeOpacity={0.25}
                stroke="#FFF"
                x={0}
                y={8.35603333}
                width={37}
                height={30}
                rx={1}
              />
              <path
                fill="url(#acheck)"
                d="M9.75585938 34.3560333L21.0314331 38.3560333 40.5586853 0 21.0314331 0 19.0314331 27.3560333 3.49880981 20.9473114z"
              />
            </g>
          </svg>
          <h3>CLOUD SECURITY &amp; COMPLIANCE</h3>
        </div>

        <div className={style.includeitem} id="vminc">
          <svg width="42px" height="40px" viewBox="0 0 42 40">
            <defs>
              <linearGradient
                x1="14.9482157%"
                y1="84.9243045%"
                x2="85.3774435%"
                y2="16.3295925%"
                id="acheck"
              >
                <stop stopColor="#1D8DBA" offset="0%" />
                <stop stopColor="#03ECAE" offset="100%" />
              </linearGradient>
            </defs>
            <g
              transform="translate(1 .644)"
              stroke="none"
              strokeWidth={1}
              fill="none"
              fillRule="evenodd"
            >
              <rect
                strokeOpacity={0.25}
                stroke="#FFF"
                x={0}
                y={8.35603333}
                width={37}
                height={30}
                rx={1}
              />
              <path
                fill="url(#acheck)"
                d="M9.75585938 34.3560333L21.0314331 38.3560333 40.5586853 0 21.0314331 0 19.0314331 27.3560333 3.49880981 20.9473114z"
              />
            </g>
          </svg>
          <h3>VULNERABILITY MANAGEMENT</h3>
        </div>
      </div>
      <div className={style.slideControls}>
        <svg width="57px" height="385px" viewBox="0 0 57 385">
          <defs>
            <linearGradient
              x1="47.4778139%"
              y1="84.9243045%"
              x2="52.5456193%"
              y2="16.3295925%"
              id="aslider"
            >
              <stop stopColor="#1D8DBA" offset="0%" />
              <stop stopColor="#03ECAE" offset="100%" />
            </linearGradient>
            <filter
              x="-214.3%"
              y="-55.5%"
              width="528.6%"
              height="211.1%"
              filterUnits="objectBoundingBox"
              id="bslider"
            >
              <feGaussianBlur stdDeviation={9} in="SourceGraphic" />
            </filter>
          </defs>
          <g
            transform="translate(-1293 -89) translate(1315.81 109.621)"
            stroke="none"
            strokeWidth={1}
            fill="none"
            fillRule="evenodd"
          >
            <rect
              fill="#D8D8D8"
              opacity={0.116203757}
              x={0}
              y={63.010279}
              width={12.5982695}
              height={48.6079295}
              rx={6.29913473}
            />
            <rect
              fill="#D8D8D8"
              opacity={0.116203757}
              x={-3.64153152e-14}
              y={1.010279}
              width={12.5982695}
              height={48.6079295}
              rx={6.29913473}
            />
            <rect
              fill="#D8D8D8"
              opacity={0.116203757}
              x={0}
              y={126.020558}
              width={12.5982695}
              height={48.6079295}
              rx={6.29913473}
            />
            <rect
              fill="#D8D8D8"
              opacity={0.116203757}
              x={0}
              y={189.030837}
              width={12.5982695}
              height={48.6079295}
              rx={6.29913473}
            />
            <rect
              fill="#D8D8D8"
              opacity={0.116203757}
              x={0}
              y={252.041116}
              width={12.5982695}
              height={48.6079295}
              rx={6.29913473}
            />
            <rect
              fill="#D8D8D8"
              opacity={0.116203757}
              x={0}
              y={315.051395}
              width={12.5982695}
              height={48.6079295}
              rx={6.29913473}
            />
            <rect
              fill="url(#aslider)"
              filter="url(#bslider)"
              x={0}
              y={0}
              width={12.5982695}
              height={48.6079295}
              rx={6.29913473}
              className="mover"
            />
            <rect
              fill="url(#aslider)"
              x={0}
              y={0}
              width={12.5982695}
              height={48.6079295}
              rx={6.29913473}
              className="mover"
            />
          </g>
        </svg>
      </div>
      <div className={style.slides}>
        <div className={style.slide} id="slide1">
          <div className={style.slideHeader}>Vulnerability Management</div>
          <div className={style.slideContent}>
            <GatsbyImage
              image={data.vmimage.childImageSharp.gatsbyImageData}
              alt="Vulnerability Management"
              className={clsx(style.noflex, style.threat)}
            />
            <div className={style.slideDesc} id="slide1text">
              <div className={style.subHeader}>AWS Inspector</div>
              <div className={style.text}>
                <div style={{ display: `flex`, alignItems: 'center' }}>
                  Automated and on-demand security and vulnerability scanning for EC2 instances with
                  built-in reporting, simple pricing, and predefined packages for industry-standard
                  compliance frameworks.
                </div>
              </div>
              <div className={style.subHeader}>POWER-UP WITH TENABLE NESSUS</div>
              <div className={style.text}>
                Industry-recognized authenticated and unauthenticated vulnerability scanning, with
                agentless design and broad extensibility, all backed by Observian’s cloud security
                and engineering expertise.
              </div>
            </div>
          </div>
        </div>
        <div className={style.slide} id="slide2">
          <div className={style.slideHeader}>Cloud Security Best Practices &amp; Compliance</div>
          <div className={style.slideContent}>
            <GatsbyImage
              image={data.compliance.childImageSharp.gatsbyImageData}
              alt="Cloud Security Best Practices and Compliance"
              className={clsx(style.noflex, style.threat)}
            />
            <div className={style.slideDesc} id="slide2text">
              <div className={style.subHeader}>
                <GatsbyImage
                  image={data.cloudcheckr.childImageSharp.gatsbyImageData}
                  alt="CloudCheckr"
                  className={clsx(style.noflex, style.threat)}
                />
                - AWS RESOURCE INVENTORY VISIBILITY
              </div>
              <div className={style.text}>
                ML-enhanced behavioral analytics from the AWS APIs, Hosts, and Containers identifies
                anomalous activity without configuration overhead or human intervention. <br />
                <br /> - Coverage for API, endpoint, and network anomalies <br /> - Context aware,
                gathering metadata and tags for AWS resources
              </div>
              <div className={style.subHeader}>
                <GatsbyImage
                  image={data.lacework.childImageSharp.gatsbyImageData}
                  alt="Lacework"
                  className={clsx(style.noflex, style.threat)}
                />
                - AWS Security Best Practices & Compliance Monitoring
              </div>
              <div className={style.text}>
                Rest assured with Observian’s 24/7 event triage and response, backed by offensive
                security experts and AWS certified security specialists.
                <br />
                <br /> - Reduce false-positives
                <br /> - Get detailed remediation recommendations from hardened security experts
              </div>
            </div>
          </div>
        </div>
        <div className={style.slide} id="slide3">
          <div className={style.slideHeader}>Threat Detection &amp; Response</div>
          <div className={style.slideContent}>
            <GatsbyImage
              image={data.threat.childImageSharp.gatsbyImageData}
              alt="Threat Detection and Response"
              className={clsx(style.noflex, style.threat)}
            />
            <div className={style.slideDesc} id="slide3text">
              <div className={style.subHeader}>Lacework Threat Detection</div>
              <div className={style.text}>
                ML-enhanced behavioral analytics from the AWS APIs, Hosts, and Containers identifies
                anomalous activity without configuration overhead or human intervention.
                <br /> - Coverage for API, endpoint, and network anomalies
                <br /> - Context aware, gathering metadata and tags for AWS resources
              </div>
              <div className={style.subHeader}>Observian 24/7 MDR</div>
              <div className={style.text}>
                Rest assured with Observian’s 24/7 event triage and response, backed by offensive
                security experts and AWS certified security specialists.
                <br /> - Reduce false-positives
                <br /> - Get detailed remediation recommendations from hardened security experts
              </div>
            </div>
          </div>
        </div>
        <div className={style.slide} id="slide4">
          <div className={style.slideHeader}>Network Security</div>
          <div className={style.slideContent}>
            <GatsbyImage
              image={data.network.childImageSharp.gatsbyImageData}
              alt="Network Security"
              className={clsx(style.noflex, style.hostandendpoint)}
            />
            <div className={style.slideDesc} id="slide4text">
              <div className={style.subHeader}>DDOS PROTECTION</div>
              <div className={style.text}>
                <div style={{ display: `flex`, alignItems: 'center' }}>
                  <svg width="51px" height="52px" viewBox="0 0 51 52" style={{ flex: 'none' }}>
                    <path
                      d="M5.755 29.705H3.6v-1.8h2.159c.18-4.554 1.656-8.887 4.327-12.58l1.46 1.056c-2.45 3.384-3.808 7.352-3.987 11.524h2.34v1.8H7.555c.167 4.196 1.527 8.188 3.99 11.59l-1.46 1.056C7.4 38.64 5.923 34.282 5.755 29.705zm36.548 17.848c-3.7 2.68-8.043 4.156-12.608 4.33v2.126h-1.8v-2.125c-4.563-.175-8.908-1.651-12.608-4.331l1.054-1.458c3.393 2.456 7.372 3.815 11.555 3.988v-2.375h1.8v2.375c4.182-.173 8.16-1.533 11.552-3.988l1.055 1.458zm-27.016-37.43c3.7-2.68 8.045-4.156 12.609-4.33V3.601h1.8v2.192c4.564.174 8.906 1.651 12.607 4.33l-1.055 1.458c-3.392-2.455-7.37-3.815-11.553-3.987v2.308h-1.8V7.594c-4.182.172-8.161 1.532-11.554 3.987l-1.054-1.458zm38.705 17.782v1.8h-2.158c-.167 4.577-1.645 8.935-4.33 12.646l-1.459-1.056c2.462-3.402 3.823-7.394 3.99-11.59h-2.342v-1.8h2.339c-.18-4.172-1.538-8.14-3.987-11.524l1.46-1.056c2.67 3.693 4.147 8.026 4.327 12.58h2.16zM42.27 14.087l7.055-7.057 1.272 1.273-7.055 7.057-1.272-1.273zM15.32 43.59l-7.055 7.057-1.273-1.273 7.055-7.057 1.273 1.273zm2.003-24.986L3.97 5.245l1.272-1.273 13.355 13.359-1.273 1.272zM40.23 38.97l13.39 13.394-1.273 1.273-13.39-13.393 1.273-1.274zm-18.936-9.504c.068-.12.14-.24.217-.36 1.513-2.378 1.079-5.622.599-7.582 1.296.853 2.465 2.614 2.864 3.354.17.314.5.503.863.471a.9.9 0 00.78-.598c1.339-3.807.658-6.67-.287-8.525a6.494 6.494 0 012.62 2.85c1.275 2.648.991 6.273-.741 9.459-2.366 4.353-1.892 8.917-1.354 11.372-1.35-.582-2.552-1.24-3.59-1.964-2.724-1.9-3.59-5.621-1.97-8.477zm12.086-.303a.902.902 0 001.453.844c.073-.058 1.549-1.277 2.263-4.225.861 1.253 1.762 3.782.563 8.426-1.186 4.596-6.812 5.888-8.82 6.206-.484-1.74-1.468-6.57.952-11.02 1.717-3.16 2.172-6.732 1.312-9.68 1.629 1.914 3.003 4.926 2.277 9.449zm-13.653-.583c-2.072 3.654-.97 8.415 2.508 10.84 1.575 1.098 3.483 2.052 5.67 2.834a.89.89 0 00.303.053c.019 0 .035-.01.053-.01l.001.007c.382-.03 9.376-.81 11.14-7.646 2.269-8.79-2.276-11.398-2.47-11.505a.9.9 0 00-1.327.697 11.608 11.608 0 01-.285 1.626c-.623-6.902-6.093-9.71-7.2-10.21-1.133-.87-2.512-1.466-4.11-1.75a.902.902 0 00-.733 1.577c.135.115 2.939 2.547 2.139 7.176-1.045-1.371-2.708-3.024-4.664-3.024a.902.902 0 00-.84 1.225c.02.049 1.854 4.884.08 7.67-.093.147-.183.295-.265.44z"
                      transform="translate(-3 -3)"
                      fill="#FFF"
                      stroke="none"
                      strokeWidth={1}
                      fillRule="evenodd"
                    />
                  </svg>
                  <span>
                    AWS Shield Advanced - Superior DDoS prevention and mitigation directly from AWS
                  </span>
                </div>
              </div>
              <div className={style.subHeader}>MANAGED NIDS</div>
              <div className={style.text}>
                Lacework behavioral analytics monitors all network traffic on each agent, allowing
                for broad visibility of network threats and anomalies. - Monitor network activity,
                with correlations to applications or users - Track indicators of compromise with DNS
                resolution tracking and connection monitoring to unmanaged destinations - Identify
                hosts with no agent present
              </div>
            </div>
          </div>
        </div>
        <div className={style.slide} id="slide5">
          <div className={style.slideHeader}>HOST &amp; ENDPOINT SECURITY</div>
          <div className={style.slideContent}>
            <GatsbyImage
              image={data.hostandendpoint.childImageSharp.gatsbyImageData}
              alt="Host and Endpoint Security"
              className={clsx(style.noflex, style.hostandendpoint)}
            />
            <div className={style.slideDesc} id="slide5text">
              <div className={style.subHeader}>LACEWORK BEHAVIORAL ANALYTICS FOR AWS ENDPOINTS</div>
              <div className={style.text}>
                Full support for AWS published Linux distributions; Amazon Linux, RedHat, CentOS,
                and Ubuntu <br /> <br />
                EC2 and VPC Metadata aware; filter events and findings by tag, VPC, region, or other
                metadata <br /> <br />
                Integrates with EKS, ECS, and Fargate for easy container integration <br />
              </div>
            </div>
          </div>
        </div>
        <div className={style.slide} id="slide6">
          <div className={style.slideHeader}>Application Security</div>
          <div className={style.slideContent}>
            <GatsbyImage
              image={data.appsecimage.childImageSharp.gatsbyImageData}
              alt="Application Security"
              className={style.noflex}
            />
            <div className={style.slideDesc} id="slide6text">
              <div className={style.subHeader}>Observian Managed WAF</div>
              <div className={style.text}>
                Observian Professional Services can assist with enabling, configuring, tuning, and
                monitoring AWS Web Application Firewall for custom applications, including managed
                or custom rulesets and traffic analysis.
                <div style={{ display: `flex`, marginTop: '50px', alignItems: 'center' }}>
                  <svg width="51px" height="52px" viewBox="0 0 51 52" style={{ flex: 'none' }}>
                    <path
                      d="M5.755 29.705H3.6v-1.8h2.159c.18-4.554 1.656-8.887 4.327-12.58l1.46 1.056c-2.45 3.384-3.808 7.352-3.987 11.524h2.34v1.8H7.555c.167 4.196 1.527 8.188 3.99 11.59l-1.46 1.056C7.4 38.64 5.923 34.282 5.755 29.705zm36.548 17.848c-3.7 2.68-8.043 4.156-12.608 4.33v2.126h-1.8v-2.125c-4.563-.175-8.908-1.651-12.608-4.331l1.054-1.458c3.393 2.456 7.372 3.815 11.555 3.988v-2.375h1.8v2.375c4.182-.173 8.16-1.533 11.552-3.988l1.055 1.458zm-27.016-37.43c3.7-2.68 8.045-4.156 12.609-4.33V3.601h1.8v2.192c4.564.174 8.906 1.651 12.607 4.33l-1.055 1.458c-3.392-2.455-7.37-3.815-11.553-3.987v2.308h-1.8V7.594c-4.182.172-8.161 1.532-11.554 3.987l-1.054-1.458zm38.705 17.782v1.8h-2.158c-.167 4.577-1.645 8.935-4.33 12.646l-1.459-1.056c2.462-3.402 3.823-7.394 3.99-11.59h-2.342v-1.8h2.339c-.18-4.172-1.538-8.14-3.987-11.524l1.46-1.056c2.67 3.693 4.147 8.026 4.327 12.58h2.16zM42.27 14.087l7.055-7.057 1.272 1.273-7.055 7.057-1.272-1.273zM15.32 43.59l-7.055 7.057-1.273-1.273 7.055-7.057 1.273 1.273zm2.003-24.986L3.97 5.245l1.272-1.273 13.355 13.359-1.273 1.272zM40.23 38.97l13.39 13.394-1.273 1.273-13.39-13.393 1.273-1.274zm-18.936-9.504c.068-.12.14-.24.217-.36 1.513-2.378 1.079-5.622.599-7.582 1.296.853 2.465 2.614 2.864 3.354.17.314.5.503.863.471a.9.9 0 00.78-.598c1.339-3.807.658-6.67-.287-8.525a6.494 6.494 0 012.62 2.85c1.275 2.648.991 6.273-.741 9.459-2.366 4.353-1.892 8.917-1.354 11.372-1.35-.582-2.552-1.24-3.59-1.964-2.724-1.9-3.59-5.621-1.97-8.477zm12.086-.303a.902.902 0 001.453.844c.073-.058 1.549-1.277 2.263-4.225.861 1.253 1.762 3.782.563 8.426-1.186 4.596-6.812 5.888-8.82 6.206-.484-1.74-1.468-6.57.952-11.02 1.717-3.16 2.172-6.732 1.312-9.68 1.629 1.914 3.003 4.926 2.277 9.449zm-13.653-.583c-2.072 3.654-.97 8.415 2.508 10.84 1.575 1.098 3.483 2.052 5.67 2.834a.89.89 0 00.303.053c.019 0 .035-.01.053-.01l.001.007c.382-.03 9.376-.81 11.14-7.646 2.269-8.79-2.276-11.398-2.47-11.505a.9.9 0 00-1.327.697 11.608 11.608 0 01-.285 1.626c-.623-6.902-6.093-9.71-7.2-10.21-1.133-.87-2.512-1.466-4.11-1.75a.902.902 0 00-.733 1.577c.135.115 2.939 2.547 2.139 7.176-1.045-1.371-2.708-3.024-4.664-3.024a.902.902 0 00-.84 1.225c.02.049 1.854 4.884.08 7.67-.093.147-.183.295-.265.44z"
                      transform="translate(-3 -3)"
                      fill="#FFF"
                      stroke="none"
                      strokeWidth={1}
                      fillRule="evenodd"
                    />
                  </svg>
                  <span>
                    AWS WAF can provide additional threat analytics based on incoming requests and
                    request log data from customer environments.
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MSSlider
